import {identity} from "lodash-es";
import {SocialNetwork, type ShareType} from "modules/enums";
import CONSTANTS from "modules/utils/constants";

interface ILeagueShare {
	socialNetwork: SocialNetwork;
	message?: string;
	locale?: string | null;
	type: ShareType.LEAGUE;
	leagueId: number;
}

type TShare = ILeagueShare;

export abstract class ShareUtility {
	private static callbacks = {
		[SocialNetwork.TWITTER]: (data: TShare) => ShareUtility.shareTW(data),
		[SocialNetwork.FACEBOOK]: (data: TShare) => ShareUtility.shareFB(data),
		[SocialNetwork.MOBILE]: (data: TShare) => ShareUtility.shareMobile(data),
	} as const;

	public static share(data: TShare) {
		const callback = ShareUtility.callbacks[data.socialNetwork];

		if (!callback || typeof callback !== "function") {
			throw new Error("Invalid share type");
		}

		callback(data);
	}

	private static shareTW({message = "", ...params}: TShare): WindowProxy | null {
		return window.open(
			"https://twitter.com/share?url=" +
				encodeURIComponent(ShareUtility.getLinkForShare(params)) +
				"&text=" +
				encodeURIComponent(message),
			"twitter-share-dialog",
			"width=626,height=436"
		);
	}

	private static shareFB(data: TShare): void {
		if (!window.FB) {
			throw new Error("No Facebook share provider found");
		}
		return window.FB?.ui(
			{
				method: "share",
				display: "popup",
				href: ShareUtility.getLinkForShare(data),
			},
			identity
		);
	}

	private static shareMobile({message = "", ...params}: TShare) {
		try {
			void navigator.share({
				url: ShareUtility.getLinkForShare(params),
				text: message,
			});
		} catch (err) {
			const error = err as Error;
			throw new Error(error.message);
		}
		return true;
	}

	private static getLinkForShare(data: TShare): string {
		const params = {
			t: this.getTimestamp(),
			// locale: data.locale,
			leagueId: data.leagueId,
		};

		return `${CONSTANTS.FB_SHARE_URL}${data.type}/${ShareUtility.getEncodedJson(params)}`;
	}

	private static getTimestamp(): string {
		const TIMESTAMP_LENGTH = 9;
		return Date.now().toString().substring(TIMESTAMP_LENGTH);
	}

	private static getEncodedJson(object: Partial<TShare> & {t: string}): string {
		return window.btoa(JSON.stringify(object));
	}
}
